import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildStudyGateProgress,
  fetchStudyGate,
  gateNameForCode,
  normalizeGateCode,
} from "./isfStudyGateService";

export default function useStudyGateView({ userData, studyId }) {
  const [studyGate, setStudyGate] = useState(null);
  const [studyGateList, setStudyGateList] = useState([]);
  const [selectedGateCodes, setSelectedGateCodes] = useState([]);
  const [gateLoading, setGateLoading] = useState(false);

  const loadGate = useCallback(async () => {
    if (!userData?.OrganizationId || !studyId) {
      setStudyGate(null);
      setStudyGateList([]);
      setSelectedGateCodes([]);
      return;
    }

    setGateLoading(true);
    try {
      const result = await fetchStudyGate({ userData, studyId });
      const state = result?.state ?? null;
      const gates = result?.gates ?? [];
      const progress = buildStudyGateProgress(gates, state);
      setStudyGate(state);
      setStudyGateList(gates);
      setSelectedGateCodes(
        progress.actualStudyGateCode ? [progress.actualStudyGateCode] : [],
      );
    } catch {
      setStudyGate(null);
      setStudyGateList([]);
      setSelectedGateCodes([]);
    } finally {
      setGateLoading(false);
    }
  }, [userData, studyId]);

  useEffect(() => {
    loadGate();
  }, [loadGate]);

  const studyGateProgress = useMemo(
    () => buildStudyGateProgress(studyGateList, studyGate),
    [studyGateList, studyGate],
  );

  /** Empty array means all phases (same as Study Management gate filter). */
  const effectiveGateCodes = useMemo(
    () =>
      (selectedGateCodes || [])
        .map((code) => normalizeGateCode(code))
        .filter(Boolean),
    [selectedGateCodes],
  );

  const phaseLabel = useMemo(() => {
    if (!effectiveGateCodes.length) return "All phases";
    return effectiveGateCodes.map((code) => gateNameForCode(code)).join(", ");
  }, [effectiveGateCodes]);

  return {
    studyGate,
    studyGateList,
    studyGateProgress,
    selectedGateCodes,
    setSelectedGateCodes,
    effectiveGateCodes,
    phaseLabel,
    gateLoading,
    reloadGate: loadGate,
  };
}
